import React, { createContext, useContext, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Snackbar, useMediaQuery } from '@mui/material';

type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';

interface SnackbarContextType {
  showSnackbar: (message: string, severity: SnackbarSeverity) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<SnackbarSeverity>('info');
  const isMobile = useMediaQuery('(max-width:768px)');

  const showSnackbar = (message: string, severity: SnackbarSeverity) => {
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
    
    // 3초 후에 자동으로 스낵바 닫기
    setTimeout(() => {
      setOpen(false);
    }, 3000);
  };

  const hideSnackbar = () => {
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      {createPortal(
        <Snackbar 
          open={open} 
          autoHideDuration={3000} 
          onClose={hideSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ 
            position: 'fixed !important',
            zIndex: '10002 !important', // UnifiedModal(1300-1400), 배너(10001)보다 높게 설정
            bottom: isMobile ? '88px' : '24px', // 모바일: 하단 네비게이션(75px) + 여백(13px)
            left: isMobile ? '16px !important' : '50% !important',
            right: isMobile ? '16px !important' : 'auto',
            transform: isMobile ? 'none !important' : 'translateX(-50%) !important',
            width: isMobile ? 'auto' : 'auto',
            maxWidth: isMobile ? 'none' : '600px',
            // 모바일에서 다이얼로그가 열려있을 때도 위에 표시되도록
            '& .MuiSnackbar-root': {
              zIndex: '10002 !important',
              position: 'fixed !important'
            },
            '& .MuiAlert-root': {
              zIndex: '10002 !important',
              boxShadow: '0 1px 2px rgba(25,35,45,0.05), 0 4px 12px rgba(25,35,45,0.04)',
              width: '100%',
              position: 'relative'
            }
          }}
        >
          <Alert 
            onClose={hideSnackbar} 
            severity={severity} 
            sx={{ 
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                width: '100%'
              },
              '& .MuiAlert-icon': {
                alignItems: 'center',
                display: 'flex'
              }
            }}
          >
            {message}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}; 